import { useFormContext } from 'react-hook-form';

import {
  ArrowDownIcon,
  ArrowUpIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

import Button, { BUTTON_ICON_POSITION, BUTTON_KIND } from '@/components/Button';
import { InfoPanelContainer } from '@/components/InfoPanelContainer';
import { FormInput } from '@/components/Input';
import ToggleField from '@/components/ToggleField';
import { IProfileSocialLink } from '@/types/IProfile';
import {
  ProperSocialsText,
  SocialsText,
  TaptSocialsSchema,
} from '@/types/TaptSocials';

import { SocialLinksForm } from './schema';

export function ProfileSocialLinks({
  disabled = false,
}: {
  disabled?: boolean;
}) {
  const { watch, setValue } = useFormContext<SocialLinksForm>();

  const profileSocialLinks = watch('profile_social_links');

  function addLink() {
    const profileLinks = [...profileSocialLinks];
    const lastOrder = profileSocialLinks.at(-1)?.order ?? -1;

    profileLinks.push({
      order: lastOrder + 1,
      type: 'facebook',
      value: '',
      personal: false,
    });
    setValue('profile_social_links', profileLinks);
  }

  function removeLink(index: number) {
    const profileLinks = [...profileSocialLinks];
    profileLinks.splice(index, 1);
    const orderedLinks = profileLinks.map((link, index) => ({
      ...link,
      order: index,
    }));
    setValue('profile_social_links', orderedLinks);
  }

  return (
    <InfoPanelContainer>
      <div className="text-base text-gray-900 font-medium capitalize pb-6 border-b border-gray-200">
        Social media links
      </div>
      <div className="space-y-3 mt-6">
        {profileSocialLinks &&
          profileSocialLinks.map((link, index) => (
            <EditSocialLink
              key={index}
              link={link}
              removeLink={() => removeLink(index)}
              isLast={profileSocialLinks.length - 1 === index}
              disabled={disabled}
            />
          ))}
        <Button
          buttonText="Add new social link"
          icon={<PlusIcon />}
          iconPos={BUTTON_ICON_POSITION.LEFT}
          kind={BUTTON_KIND.WHITE}
          onClick={addLink}
          disabled={disabled}
        />
      </div>
    </InfoPanelContainer>
  );
}

type EditSocialLinkProps = {
  link: IProfileSocialLink;
  removeLink: () => void;
  isLast: boolean;
  disabled?: boolean;
};

function EditSocialLink({
  link,
  removeLink,
  isLast,
  disabled = false,
}: EditSocialLinkProps) {
  const {
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<SocialLinksForm>();

  function onTypeSelect(type: string) {
    const profileSocialLinks = getValues('profile_social_links');
    const index = profileSocialLinks.findIndex(
      item => item.order === link.order,
    );

    const validType = TaptSocialsSchema.safeParse(type);
    if (validType.success) {
      setValue(`profile_social_links.${index}.type`, validType.data);
    }
  }

  function onValueChange(value: string) {
    const profileSocialLinks = getValues('profile_social_links');
    const index = profileSocialLinks.findIndex(
      item => item.order === link.order,
    );

    setValue(`profile_social_links.${index}.value`, value);
  }

  function moveLink(direction: 'up' | 'down') {
    const profileSocialLinks = getValues('profile_social_links');
    const newOrder = direction === 'up' ? link.order - 1 : link.order + 1;

    if (newOrder < 0 || newOrder >= profileSocialLinks.length) {
      return;
    }

    const targetLinkIndex = profileSocialLinks.findIndex(
      item => item.order === newOrder,
    );
    profileSocialLinks[targetLinkIndex].order = link.order;
    link.order = newOrder;

    const updatedLinks = profileSocialLinks.sort((a, b) => a.order - b.order);

    setValue('profile_social_links', updatedLinks);
  }

  function onPersonalToggle(enabled: boolean) {
    const profileSocialLinks = getValues('profile_social_links');
    const index = profileSocialLinks.findIndex(
      item => item.order === link.order,
    );

    setValue(`profile_social_links.${index}.personal`, enabled);
  }

  function getLinkIndex() {
    const profileSocialLinks = getValues('profile_social_links');
    return profileSocialLinks.findIndex(
      item => item.value === link.value && item.order === link.order,
    );
  }

  const error = errors.profile_social_links?.[getLinkIndex()];

  return (
    <div className="flex flex-col-reverse xl:flex-row border border-gray-300 rounded-md overflow-hidden">
      <div className="flex flex-row xl:flex-col justify-end xl:justify-between space-x-2 xl:space-x-0 bg-gray-50 p-3">
        <button
          className="border border-gray-300 bg-white rounded-md p-2 disabled:opacity-40 hover:bg-gray-50"
          onClick={() => moveLink('up')}
          disabled={disabled || link.order === 0}
        >
          <ArrowUpIcon
            className="w-5 h-5 text-gray-500"
            strokeWidth={2}
            aria-hidden="true"
          />
        </button>
        <button
          className="border border-gray-300 bg-white rounded-md p-2 disabled:opacity-40 hover:bg-gray-50"
          onClick={() => moveLink('down')}
          disabled={disabled || isLast}
        >
          <ArrowDownIcon
            className="w-5 h-5 text-gray-500"
            strokeWidth={2}
            aria-hidden="true"
          />
        </button>
      </div>
      <div className="flex flex-col w-full py-4 px-6 space-y-3">
        <div className="flex flex-col xl:flex-row justify-between gap-4">
          <div className="flex flex-col space-y-1.5 mt-0.5 flex-1">
            <label
              htmlFor={`profile-social-link-${link.type}-${link.value}`}
              className="text-sm leading-5 font-medium text-gray-900"
            >
              Social channel
            </label>
            <select
              name={`profile-social-link-${link.type}-${link.value}`}
              id={`profile-social-link-${link.type}-${link.value}`}
              value={link.type}
              onChange={e => onTypeSelect(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-brand-500 focus:border-brand-500 shadow-sm disabled:cursor-not-allowed disabled:bg-gray-200"
              disabled={disabled}
            >
              {SocialsText.map((social, index) => (
                <option key={index} value={social}>
                  {ProperSocialsText[index]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col flex-1">
            <FormInput
              label="Social URL"
              type="text"
              value={link.value}
              onChange={e => onValueChange(e.target.value)}
              error={!!error?.value}
              disabled={disabled}
              required
            />
            <div className="text-sm text-gray-500 mt-2">
              Clicking the icon will go here
            </div>
            {error && error.value && (
              <span className="text-sm text-red-400 font-medium">
                {error.value.message}
              </span>
            )}
          </div>
        </div>
        <div className="flex justify-between">
          <button
            className="text-brand-500 font-medium text-sm disabled:text-gray-500"
            onClick={removeLink}
            disabled={disabled}
          >
            Remove link
          </button>
          <ToggleField
            label="Personal link"
            lableColor="gray-400"
            enabled={link.personal}
            setter={onPersonalToggle}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}
